import { Capacitor } from '@capacitor/core';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isFeatureOn } from '@/lib/appConfig';
import { captionsPrefEnabled } from '@/lib/captions';
import {
  adoptNativeVideo,
  claimNativeVideo,
  closeReason,
  nativeVideo,
  nativeVideoId,
  nativeVideoWanted,
  pageRect,
  readCoverInsets,
  releaseNativeVideo,
  releaseNativeVideoLater,
  samePlacement,
  useNativeVideoActive,
  type NativeVideoPlacement,
} from '@/lib/inlineVideoNative';
import { speedPref } from '@/lib/playbackRate';
import { isStoreBuild } from '@/lib/platform';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { openVideoOutside, videoPageUrl } from '@/lib/videoOutside';
import { WatermarkLayer } from '@/components/protection/Watermark';
import { useAuthStore } from '@/stores/auth';

/** Скругление карточки (`rounded-card` в tailwind.config) — нативный вид повторяет его. */
const CARD_RADIUS = 16;

/** Как часто сверять место карточки, пока ролик открыт: ловит то, чего не видят наблюдатели размеров. */
const PLACEMENT_POLL_MS = 500;

/** Где сейчас карточка и что её закрывает — всё, что нужно плагину. */
function placementOf(el: HTMLElement | null): NativeVideoPlacement | null {
  if (!el) return null;
  return { ...pageRect(el.getBoundingClientRect(), window), ...readCoverInsets(document, window) };
}

/**
 * Видео в приложении: обложка в карточке, по нажатию — ролик.
 *
 * ⚠️ ВЕТКА video-native-webview (16.09.2026, техдолг п. 232), пробы 58–60. Под флагом `video_native` видеоблок урока
 * (`native`) открывает ролик в ОТДЕЛЬНОМ нативном WKWebView поверх карточки (`lib/inlineVideoNative.ts`,
 * `ios/App/App/InlineVideoPlugin.swift`). Живой вид один: нажал другой ролик — прежний закрывается, новый открывается
 * на месте своей карточки. Пока ролик открыт, карточка сверяет своё место и отступы панелей и сообщает плагину.
 * Ушёл со страницы — вид закрывается. Материалы — прежним окном.
 *
 * 🔴 РАМКА В ОСНОВНОМ WebView — ТУПИК, ПРИЧИНА УСТАНОВЛЕНА (попытка 9, сборка 57): процесс страницы убивается ядром
 * по памяти, jetsam `PERPROCESSLIMIT`. Поэтому здесь не iframe, а свой WKWebView со своим процессом.
 *
 * ⚠️ Вставить YouTube напрямую нельзя: источник приложения `capacitor://`, Referer не уходит, YouTube отвечает
 * ошибкой 153. И в окне, и в нативном виде — наша страница на настоящем адресе.
 *
 * ⚠️ НЕ ОТКРЫЛОСЬ — ЧЕСТНАЯ ОШИБКА НА КАРТОЧКЕ, А НЕ SAFARI (14.09.2026, Apple 3.1.3(f)).
 *
 * Журнал попыток: docs/platform/06-frontend/05-video-in-app-attempts.md
 */
export function InlineYouTube({
  videoId,
  title,
  native = false,
}: {
  videoId: string;
  title?: string;
  /** Видеоблок урока — может играть в нативном виде. Материалы его не передают. */
  native?: boolean;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [failed, setFailed] = useState(false);
  // Имя вида устойчивое (страница + ролик), а не `useId`: карточка, пересозданная перерисовкой урока, должна узнать
  // свой ещё открытый ролик. `cardId` — сам экземпляр: видом владеет ровно один.
  const cardId = useId();
  const nativeId = nativeVideoId(window.location.pathname, videoId);
  const live = useNativeVideoActive(nativeId, cardId);
  const lastPlacement = useRef<NativeVideoPlacement | null>(null);
  const cardRef = useRef<HTMLButtonElement>(null);

  const nativeWanted = nativeVideoWanted({
    native,
    store: isStoreBuild(),
    platform: Capacitor.getPlatform(),
    flag: isFeatureOn('video_native'),
  });

  // Ушёл со страницы — вид закрываем: он лежит в прокрутке ОСНОВНОГО WebView и сам со страницей урока не исчезнет.
  // Закрываем только СВОЙ: карточка, которую уже вытеснил другой ролик, чужой вид не трогает.
  // ⚠️ ПЕРЕРИСОВКА — НЕ УХОД (01.10.2026): закрытие отложено, и такая же карточка, созданная в том же проходе,
  // забирает открытый ролик себе. Разбор — `releaseNativeVideoLater` в lib/inlineVideoNative.ts.
  useEffect(() => {
    adoptNativeVideo(nativeId, cardId);
    return () => {
      releaseNativeVideoLater(nativeId, cardId, () => {
        nativeVideo.close({ id: nativeId, reason: closeReason('unmount', window) }).catch(() => undefined);
      });
    };
  }, [nativeId, cardId]);

  // Пока ролик живой — сверять место карточки: подгрузилась картинка выше, раскрылся блок, повернули телефон,
  // появилась полоска записи. Сообщаем только изменения.
  useEffect(() => {
    if (!live) return;
    const push = () => {
      const p = placementOf(cardRef.current);
      if (!p || samePlacement(lastPlacement.current, p)) return;
      lastPlacement.current = p;
      nativeVideo.update({ id: nativeId, ...p }).catch((err: unknown) => {
        console.error('[InlineVideo] native update failed', err);
      });
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(push);
    if (observer && cardRef.current) {
      observer.observe(cardRef.current);
      // `body` высотой в экран, содержимое растёт в оболочке приложения — её и смотрим.
      const shell = document.getElementById('root')?.firstElementChild;
      if (shell) observer.observe(shell);
    }
    window.addEventListener('resize', push);
    window.addEventListener('orientationchange', push);
    const timer = window.setInterval(push, PLACEMENT_POLL_MS);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', push);
      window.removeEventListener('orientationchange', push);
      window.clearInterval(timer);
    };
  }, [live, nativeId]);

  // ⚠️ Подпись знака идёт ЯКОРЕМ (`#m=`): якорь браузер на сервер не отправляет,
  // и почта ученика не попадает в журналы nginx.
  const mark = useMemo(
    () => [user?.id?.slice(0, 8) ?? 'anon', user?.email ?? ''].filter(Boolean).join(' · '),
    [user?.id, user?.email],
  );
  // ⚠️ В приложении знака нет (D62, 26.09.2026, сборка 61): странице плеера — `nomark=1` и никакой подписи, почта
  // ученика в адрес не уходит. Запись экрана закрывает система (шторка iOS, FLAG_SECURE на Android).
  const pageMark = IS_APP_BUILD ? undefined : mark;

  const open = () => {
    setFailed(false);
    // Выбор про субтитры и скорость едет параметрами адреса: у страницы плеера ДРУГОЙ источник,
    // хранилище приложения ей не видно. Это лишь умолчания.
    const captions = captionsPrefEnabled();
    const speed = speedPref();

    if (nativeWanted) {
      const p = placementOf(cardRef.current);
      if (!p) return;
      // Сначала занять место: прежняя карточка перестаёт быть живой и больше не шлёт свои `update`.
      claimNativeVideo(nativeId, cardId);
      lastPlacement.current = p;
      const url = videoPageUrl(videoId, pageMark, true, IS_APP_BUILD, captions, speed, true);
      nativeVideo.open({ id: nativeId, url, ...p, radius: CARD_RADIUS }).catch((err: unknown) => {
        console.error('[InlineVideo] native open failed', err);
        releaseNativeVideo(nativeId, cardId);
        setFailed(true);
      });
      return;
    }

    openVideoOutside(videoPageUrl(videoId, pageMark, true, IS_APP_BUILD, captions, speed)).catch((err: unknown) => {
      console.error('[InlineVideo] window open failed', err);
      setFailed(true);
    });
  };

  return (
    <button
      // «Играть» внутри задания — не начало ответа (окно «Выйти из урока?», lib/lessonAnswerGuard.ts).
      data-no-dirty=""
      ref={cardRef}
      type="button"
      onClick={open}
      className="relative block aspect-video w-full overflow-hidden rounded-card bg-black"
      aria-label={title || 'video'}
    >
      <img
        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover opacity-90"
      />
      <span className="absolute inset-0 grid place-items-center">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-primary text-on-primary shadow-ambient">
          <span className="material-symbols-outlined text-[30px]">play_arrow</span>
        </span>
      </span>
      {failed && (
        <span role="alert" className="absolute inset-x-0 bottom-0 bg-black/75 px-3 py-2 text-center text-sm text-white">
          {t('materials.videoOpenFailed')}
        </span>
      )}
      <WatermarkLayer />
    </button>
  );
}
